import { useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export type WalkthroughEventType =
    | 'view_slide'
    | 'complete_slide'
    | 'skip'
    | 'complete_tour'
    | 'dismiss_tip';

export type WalkthroughPlatform = 'mobile' | 'desktop';

interface LogEventParams {
    eventType: WalkthroughEventType;
    stepId: string;
    platform: WalkthroughPlatform;
}

/**
 * Hook for logging walkthrough analytics events to Supabase.
 * Events are used to understand user onboarding behavior and drop-off points.
 */
export function useWalkthroughAnalytics() {
    const logEvent = useCallback(async ({ eventType, stepId, platform }: LogEventParams) => {
        try {
            // Check for cookie consent
            const consentString = localStorage.getItem('ae-cookie-consent');
            if (consentString) {
                try {
                    const consent = JSON.parse(consentString);
                    // If analytics consent is explicitly false, do not log
                    if (consent.analytics === false) {
                        if (import.meta.env.DEV) {
                            console.debug('[WalkthroughAnalytics] Skipping log (no consent):', eventType);
                        }
                        return;
                    }
                } catch (e) {
                    console.warn('Error parsing cookie consent:', e);
                }
            }

            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return; // Skip logging for unauthenticated users

            await supabase.from('walkthrough_events').insert({
                user_id: user.id,
                event_type: eventType,
                step_id: stepId,
                platform,
            });
        } catch (error) {
            // Silently fail - analytics should never block UX
            console.warn('[WalkthroughAnalytics] Failed to log event:', error);
        }
    }, []);

    return { logEvent };
}
